import {
  Action as WhatToDoAction,
  Feature as WhatToDoFeature,
  type ResultOption,
} from '@/apis/core/search/whatToDo/post/postWhatToDo';

/**
 * Maps canonical API action values to short UI labels (Create / Open).
 */
export function formatWhatToDoActionLabel(
  action: ResultOption['action'] | string | undefined,
): string {
  const a = String(action ?? '').toLowerCase();
  if (a === WhatToDoAction.CREATE) return 'Create';
  if (a === WhatToDoAction.OPEN) return 'Open';
  return a ? a.charAt(0).toUpperCase() + a.slice(1) : '';
}

/**
 * Maps canonical API feature values to short UI labels (Ask, Workflow, …).
 */
export function formatWhatToDoFeatureLabel(
  feature: ResultOption['feature'] | string | undefined,
): string {
  const f = String(feature ?? '').toLowerCase();
  switch (f) {
    case WhatToDoFeature.ASK:
      return 'Ask';
    case WhatToDoFeature.COLLECTION:
      return 'Collection';
    case WhatToDoFeature.DOCUMENT:
      return 'Document';
    case WhatToDoFeature.PERSONA:
      return 'Persona';
    case WhatToDoFeature.PROMPT:
      return 'Prompt';
    case WhatToDoFeature.WORKFLOWINSTANCE:
      return 'Workflow';
    case WhatToDoFeature.WORKSPACE:
      return 'Workspace';
    default:
      return f ? f.charAt(0).toUpperCase() + f.slice(1) : '';
  }
}

/**
 * Bold list title for structured suggested actions (compact QuestionAssistant).
 */
export function buildSuggestedActionTitle(option: ResultOption): string {
  const actionLabel = formatWhatToDoActionLabel(option.action);
  const featureLabel = formatWhatToDoFeatureLabel(option.feature);
  if (actionLabel && featureLabel) {
    return `${actionLabel} · ${featureLabel}`;
  }
  return actionLabel || featureLabel || 'Suggested next step';
}

export type SuggestedActionPayload = ResultOption & {
  workspaceName?: string;
  title?: string;
};

/**
 * Shape consumed by QuestionAssistant `suggestedActionsData`.
 * Keeps canonical `action` / `feature` strings from the API so routing (`executeOption`)
 * continues to match enums after `.toLowerCase()`.
 */
export function mapResultOptionToSuggestedAction(
  option: ResultOption,
  workspaceName?: string,
): SuggestedActionPayload {
  return {
    ...option,
    ...(workspaceName ? { workspaceName } : {}),
    title: buildSuggestedActionTitle(option),
  };
}

/**
 * Resolve the selected row from QuestionAssistant back to a {@link ResultOption}.
 */
export function resolveResultOptionFromSuggestedSelection(
  selected: SuggestedActionPayload | ResultOption,
  fallbacks: ResultOption[],
): ResultOption | undefined {
  const wid = selected.workspaceId;
  const id = selected.id;
  if (wid != null && id != null) {
    const byWsAndId = fallbacks.find(
      (o) => o.workspaceId === wid && o.id === id,
    );
    if (byWsAndId) return byWsAndId;
  }
  if (wid != null) {
    const match = fallbacks.find(
      (o) =>
        o.workspaceId === wid &&
        String(o.action).toLowerCase() ===
          String(selected.action ?? '').toLowerCase() &&
        String(o.feature).toLowerCase() ===
          String(selected.feature ?? '').toLowerCase(),
    );
    if (match) return match;
  }
  return fallbacks.length === 1 ? fallbacks[0] : undefined;
}
